/**
 * UserSettingsPanel — Modal de configuración avanzada del usuario
 *
 * QUÉ HACE:
 * - Modal centrado con configuración personal del usuario (portal en document.body)
 * - Funcional: tema, notificaciones, voz/sonido, sesión activa, cambio de contraseña
 * - Sub-vistas: "main" | "change-password" | "notifications" | "voice" | "devices" | "pos-hardware"
 *
 * QUÉ INVOCA: useAuthStore, useLogout, useThemeStore, authService, toast, usePermissions
 * QUÉ EXPORTA: UserSettingsPanel (named)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe hacer llamadas a API fuera de handleSendReset (usa authService.forgotPassword)
 * - NO debe duplicar lógica de logout (usa useLogout)
 * - NO debe manejar permisos de módulos de negocio (salvo la exclusión dueño→POS global abajo)
 * - NO debe permitir edición de nombre/perfil desde aquí (ver decisión arquitectónica abajo)
 *
 * ══════════════════════════════════════════════════════════════════
 * DECISIÓN ARQUITECTÓNICA — Editar perfil y foto: DESHABILITADO
 * ══════════════════════════════════════════════════════════════════
 * PROBLEMA: El nombre del usuario existe en DOS fuentes independientes:
 *   1. authStore (Zustand/localStorage) → lo que ve el sidebar y este modal
 *   2. employees mock/BD → lo que usan Nómina, Asistencia, Auditoría, etc.
 *
 * Si se editara solo el authStore, el sidebar mostraría "Carlos García"
 * mientras que Nómina seguiría mostrando "Carlos Ruiz" → datos zombi.
 *
 * SOLUCIÓN FUTURA (cuando haya backend real):
 *   - Un único endpoint PATCH /usuarios/:id actualiza nombre en la BD
 *   - authStore se recarga desde el server tras el update
 *   - Todos los módulos que consulten la BD recibirán el nombre actualizado
 *   - No habrá que cambiar ningún componente frontend
 *
 * FOTO DE PERFIL: También deshabilitada. Requiere storage externo
 * (Firebase Storage / S3 / Cloudinary) con costos adicionales.
 * El avatar de inicial es suficiente hasta conectar el backend.
 *
 * SEGURIDAD: Permitir que empleados cambien su propio nombre visible
 * puede generar confusión en logs de auditoría y riesgo de suplantación.
 * El nombre debe ser gestionado únicamente por el dueño/admin.
 * ══════════════════════════════════════════════════════════════════
 *
 * REGLAS:
 * - Todo dato real del usuario viene de useAuthStore
 * - Nuevas secciones se agregan aquí sin tocar Sidebar
 * - Las sub-vistas se controlan con estado local `view`
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Shield,
  Bell,
  Moon,
  Sun,
  LogOut,
  Key,
  Smartphone,
  Monitor,
  Wifi,
  ShoppingCart,
  Package,
  Users as UsersIcon,
  AlertCircle,
  Settings,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Check,
  Clock,
  Mic,
  Volume2,
  BookOpen,
  Printer,
  Activity,
  FileText,
  HardDrive,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useLogout, usePermissions } from "@/hooks";
import { useThemeStore } from "@/store/themeStore";
import { useNotificationPrefsStore } from "@/store/notificationPrefsStore";
import { authService } from "@/services/authService";
import { auditoriaService } from "@/features/auditoria/services/auditoria.service";
import { printHealthReport } from "@/lib/persistence/diagnostics";
import toast from "react-hot-toast";
import { ConfirmationModal } from "@/components/common/ConfirmationModal";
import {
  buildExampleActivationNotification,
  MobileNotificationPreview,
} from "@/lib/push";
import { UserSettingsPosHardware } from "@/features/caja/components/UserSettingsPosHardware";
import { UserSettingsCompliance } from "@/components/navigation/UserSettingsCompliance";
import { UserSettingsStorage } from "@/components/navigation/UserSettingsStorage";

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface UserSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sidebarExpanded?: boolean;
  /** Sub-vista solicitada al abrir (ej. "storage" desde el aviso de memoria). */
  initialView?: PanelView;
  /** Notifica que la sub-vista inicial fue consumida (para resetear en el padre). */
  onInitialViewConsumed?: () => void;
}

export type PanelView =
  | "main"
  | "change-password"
  | "notifications"
  | "voice"
  | "devices"
  | "pos-hardware"
  | "compliance"
  | "storage";

// ─── Constantes ──────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  dueño: "Dueño",
  administrador: "Administrador",
  cajero: "Cajero",
  vendedor: "Vendedor",
  empleado: "Empleado",
};

const ROLE_COLORS: Record<string, string> = {
  dueño:
    "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  administrador:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  cajero:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  vendedor:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  empleado: "bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300",
};

const SUB_VIEW_TITLES: Record<PanelView, string> = {
  main: "",
  "change-password": "Cambiar contraseña",
  notifications: "Notificaciones",
  voice: "Voz y sonido",
  devices: "Sesiones activas",
  "pos-hardware": "POS, gaveta y seguridad",
  compliance: "Cumplimiento Legal y Bóveda",
  storage: "Almacenamiento y respaldo",
};

/** Detecta browser y OS del dispositivo actual usando el user-agent */
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const browser = ua.includes("Edg")
    ? "Edge"
    : ua.includes("Chrome")
      ? "Chrome"
      : ua.includes("Firefox")
        ? "Firefox"
        : ua.includes("Safari")
          ? "Safari"
          : "Navegador";
  const os = ua.includes("Windows")
    ? "Windows"
    : ua.includes("Mac")
      ? "macOS"
      : ua.includes("Android")
        ? "Android"
        : ua.includes("iPhone") || ua.includes("iPad")
          ? "iOS"
          : ua.includes("Linux")
            ? "Linux"
            : "Desconocido";
  const isMobile = /Android|iPhone|iPad/i.test(ua);
  return { browser, os, isMobile };
};

// ─── Componentes internos ─────────────────────────────────────────────────────

/** Toggle switch visual — pointer-events deshabilitados (el click va en el SettingsItem padre) */
const ToggleSwitch = ({ checked }: { checked: boolean }) => (
  <div
    className={`relative w-8 h-4 rounded-full flex-shrink-0 transition-colors duration-200 pointer-events-none
      ${checked ? "bg-primary-500" : "bg-gray-300 dark:bg-slate-600"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200
        ${checked ? "translate-x-4" : "translate-x-0"}`}
    />
  </div>
);

const SettingsItem = ({
  icon: Icon,
  label,
  description,
  onClick,
  badge,
  disabled,
  rightElement,
}: {
  icon: React.ElementType;
  label: string;
  description?: string;
  onClick?: () => void;
  badge?: string;
  disabled?: boolean;
  /** Reemplaza el chevron/badge por un elemento personalizado (ej: toggle switch) */
  rightElement?: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-150 group text-left
      ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:bg-gray-50 dark:hover:bg-slate-700/60 active:scale-[0.99]"
      }`}
  >
    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
      <Icon
        size={14}
        className="text-gray-500 dark:text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
      />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-gray-800 dark:text-slate-200">
        {label}
      </p>
      {description && (
        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5 leading-tight">
          {description}
        </p>
      )}
    </div>
    {rightElement !== undefined ? (
      rightElement
    ) : badge ? (
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex-shrink-0">
        {badge}
      </span>
    ) : (
      !disabled && (
        <ChevronRight
          size={13}
          className="text-gray-300 dark:text-slate-600 flex-shrink-0 group-hover:text-primary-400 transition-colors"
        />
      )
    )}
  </button>
);

// ─── Componente principal ─────────────────────────────────────────────────────

// Categorías de notificaciones disponibles
const NOTIF_CATEGORIES = [
  {
    key: "ventas",
    icon: ShoppingCart,
    label: "Ventas",
    description: "Nuevas ventas y transacciones",
  },
  {
    key: "inventario",
    icon: Package,
    label: "Inventario",
    description: "Stock bajo y alertas de productos",
  },
  {
    key: "personal",
    icon: UsersIcon,
    label: "Personal",
    description: "Asistencia y eventos del equipo",
  },
  {
    key: "sistema",
    icon: AlertCircle,
    label: "Sistema",
    description: "Actualizaciones y alertas críticas",
  },
] as const;

type NotifKey = (typeof NOTIF_CATEGORIES)[number]["key"];

const VOICE_OPTIONS = [
  {
    key: "sonidos_ui",
    icon: Volume2,
    label: "Sonidos en la app",
    description: "Tonos al confirmar acciones (cuando estén disponibles)",
  },
  {
    key: "lectura_alertas",
    icon: BookOpen,
    label: "Lectura de alertas",
    description: "Anunciar avisos importantes por voz (futuro)",
  },
  {
    key: "entrada_voz",
    icon: Mic,
    label: "Entrada por voz",
    description: "Buscar y comandos hablados (futuro)",
  },
] as const;

type VoiceKey = (typeof VOICE_OPTIONS)[number]["key"];

export const UserSettingsPanel = ({
  isOpen,
  onClose,
  initialView,
  onInitialViewConsumed,
}: UserSettingsPanelProps) => {
  const { user } = useAuthStore();
  const { isOwner, isAdmin } = usePermissions();
  const canManagePosHardware = isOwner || isAdmin;
  const { handleLogout } = useLogout();
  const { theme, toggleTheme } = useThemeStore();

  const [view, setView] = useState<PanelView>("main");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Abrir directamente en una sub-vista solicitada (ej. botón "Liberar espacio").
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && initialView) {
      setView(initialView);
      onInitialViewConsumed?.();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, initialView, onInitialViewConsumed]);
  // Preferencias de notificaciones — persistidas en el dispositivo (notificationPrefsStore).
  // Activar una categoría pide el permiso real de notificaciones del sistema operativo.
  const notifPrefs = useNotificationPrefsStore((s) => s.prefs);
  const toggleNotifCategory = useNotificationPrefsStore(
    (s) => s.toggleCategory,
  );
  /** Confirmación al desactivar una categoría (misma lógica que otros ConfirmationModal del app) */
  const [notifConfirm, setNotifConfirm] = useState<{
    open: boolean;
    key: NotifKey | null;
  }>({ open: false, key: null });

  const [voicePrefs, setVoicePrefs] = useState<Record<VoiceKey, boolean>>({
    sonidos_ui: true,
    lectura_alertas: false,
    entrada_voz: false,
  });
  const [voiceConfirm, setVoiceConfirm] = useState<{
    open: boolean;
    key: VoiceKey | null;
  }>({ open: false, key: null });

  // Resetear sub-vistas al cerrar el modal
  useEffect(() => {
    if (!isOpen) {
      setView("main");
      setResetSent(false);
      setNotifConfirm({ open: false, key: null });
      setVoiceConfirm({ open: false, key: null });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && view === "pos-hardware" && !canManagePosHardware) {
      setView("main");
    }
  }, [isOpen, view, canManagePosHardware]);

  // Bloquear scroll del body
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Cerrar con Escape (sub-vista → volver, vista main → cerrar)
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (view !== "main") setView("main");
        else onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose, view]);

  const initial = (user?.nombre || user?.email || "U").charAt(0).toUpperCase();
  const displayName = user?.nombre || user?.email?.split("@")[0] || "Usuario";
  const roleLabel = ROLE_LABELS[user?.role ?? ""] ?? user?.role ?? "—";
  const roleColor = ROLE_COLORS[user?.role ?? ""] ?? ROLE_COLORS.empleado;

  // ─── Handlers ───────────────────────────────────────────────────────────────

  /** Envía email de restablecimiento via Firebase Auth (igual que LoginPage) */
  const handleSendReset = async () => {
    if (!user?.email) return;
    setResetLoading(true);
    try {
      await authService.forgotPassword(user.email);
      setResetSent(true);

      // Registrar en auditoría (solicitud desde el perfil)
      void auditoriaService.recordLog({
        userId: user.id,
        userName: user.nombre || "Usuario",
        action: "password_reset_request_self",
        entidad: "seguridad",
        entidadId: user.id,
        resumen: `Solicitud propia de cambio de contraseña`,
        descripcion: `El usuario solicitó un enlace para cambiar su contraseña desde el panel de ajustes de su cuenta.`,
        severity: "low",
        details: {
          accion: "solicitar_cambio_propio",
          motor: "web_browser",
          origin: "settings",
        },
      });

      toast.success("Enlace enviado. Revisa tu bandeja de entrada.", {
        duration: 5000,
      });
    } catch (error: unknown) {
      const err = error as { code?: string };
      const msg =
        err.code === "auth/user-not-found"
          ? "No hay cuenta asociada a ese correo"
          : "Error al enviar el correo. Intenta de nuevo.";
      toast.error(msg);
    } finally {
      setResetLoading(false);
    }
  };

  /**
   * Al activar: solicita el permiso real del sistema operativo (Android/iOS/
   * Escritorio/Web) y aplica al instante si lo concede.
   * Al desactivar: abre ConfirmationModal (evita cortar alertas por un clic accidental).
   */
  const requestToggleNotif = async (key: NotifKey) => {
    if (!notifPrefs[key]) {
      const granted = await toggleNotifCategory(key);
      if (granted) {
        toast.success("Notificaciones activadas para esta categoría");
      } else {
        toast.error(
          "Activa el permiso de notificaciones en los ajustes del dispositivo",
        );
      }
      return;
    }
    setNotifConfirm({ open: true, key });
  };

  const confirmDisableNotif = () => {
    const key = notifConfirm.key;
    if (key) {
      void toggleNotifCategory(key);
      toast("Preferencia guardada en este dispositivo");
    }
  };

  const closeNotifConfirm = () => setNotifConfirm({ open: false, key: null });

  const notifConfirmCopy = (() => {
    const key = notifConfirm.key;
    if (!key) return { title: "", message: "" };
    const cat = NOTIF_CATEGORIES.find((c) => c.key === key);
    const label = cat?.label ?? key;
    const title = `¿Desactivar alertas de ${label}?`;
    const message =
      key === "sistema"
        ? "No recibirás avisos de actualizaciones ni alertas críticas del sistema en este dispositivo. Puedes volver a activarlas cuando quieras."
        : `Dejarás de recibir notificaciones de «${label}» en este dispositivo hasta que las reactives.`;
    return { title, message };
  })();

  const applyVoiceToggle = (key: VoiceKey) => {
    setVoicePrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const requestToggleVoice = (key: VoiceKey) => {
    if (!voicePrefs[key]) {
      applyVoiceToggle(key);
      toast.success("Opción de voz activada");
      return;
    }
    setVoiceConfirm({ open: true, key });
  };

  const confirmDisableVoice = () => {
    const key = voiceConfirm.key;
    if (key) {
      applyVoiceToggle(key);
      toast("Preferencia de voz actualizada");
    }
  };

  const closeVoiceConfirm = () => setVoiceConfirm({ open: false, key: null });

  const voiceConfirmCopy = (() => {
    const key = voiceConfirm.key;
    if (!key) return { title: "", message: "" };
    const opt = VOICE_OPTIONS.find((c) => c.key === key);
    const label = opt?.label ?? key;
    const title = `¿Desactivar ${label}?`;
    const message =
      key === "sonidos_ui"
        ? "La app quedará en silencio para retroalimentación inmediata. Las notificaciones push no se ven afectadas aquí."
        : `Esta función aún está en preparación. Si la desactivas ahora, quedará desmarcada para cuando esté disponible.`;
    return { title, message };
  })();

  const examplePush = buildExampleActivationNotification();

  // ─── Cuerpo según vista ──────────────────────────────────────────────────────

  const renderBody = () => {
    if (view === "change-password") {
      return (
        <div className="px-4 py-4 space-y-3">
          <p className="text-xs text-gray-600 dark:text-slate-400 leading-relaxed">
            Enviaremos un enlace a tu correo para que puedas restablecer tu
            contraseña de forma segura.
          </p>

          {/* Email del usuario */}
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-600">
            <div className="w-7 h-7 rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
              <Key
                size={12}
                className="text-primary-600 dark:text-primary-400"
              />
            </div>
            <span className="text-xs text-gray-700 dark:text-slate-300 font-medium truncate">
              {user?.email}
            </span>
          </div>

          {resetSent ? (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
              <Check size={14} className="flex-shrink-0" />
              <span className="text-xs font-semibold">
                Enlace enviado. Revisa tu bandeja de entrada.
              </span>
            </div>
          ) : (
            <button
              onClick={handleSendReset}
              disabled={resetLoading}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-semibold text-xs transition-all active:scale-[0.98]"
            >
              {resetLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Key size={13} />
              )}
              {resetLoading
                ? "Enviando..."
                : "Enviar enlace de restablecimiento"}
            </button>
          )}
        </div>
      );
    }

    if (view === "notifications") {
      const activeCount = Object.values(notifPrefs).filter(Boolean).length;
      return (
        <div className="px-3 py-3 space-y-2">
          <p className="px-1 pb-1 text-[11px] text-gray-500 dark:text-slate-400 leading-relaxed">
            Elige qué alertas quieres recibir. Al activar una categoría se
            pedirá el permiso de notificaciones de tu dispositivo — a partir de
            ahí llegarán avisos reales cuando ocurra algo en el sistema (ventas,
            cierres de caja, gastos, inventario, personal).
          </p>
          <MobileNotificationPreview
            title={examplePush.title}
            body={examplePush.body}
            timeLabel="ahora"
          />
          <div className="space-y-0.5">
            {NOTIF_CATEGORIES.map(({ key, icon: Icon, label, description }) => (
              <SettingsItem
                key={key}
                icon={Icon}
                label={label}
                description={description}
                onClick={() => void requestToggleNotif(key)}
                rightElement={<ToggleSwitch checked={notifPrefs[key]} />}
              />
            ))}
          </div>
          <p className="px-1 pt-0.5 text-[10px] text-gray-400 dark:text-slate-500 text-center">
            {activeCount} de {NOTIF_CATEGORIES.length} categorías activas
          </p>
        </div>
      );
    }

    if (view === "voice") {
      const voiceActive = Object.values(voicePrefs).filter(Boolean).length;
      return (
        <div className="px-3 py-3 space-y-0.5">
          <p className="px-1 pb-2 text-[11px] text-gray-500 dark:text-slate-400 leading-relaxed">
            Controla sonidos y funciones de voz. Al desactivar una opción se
            pedirá confirmación (misma lógica que en notificaciones).
          </p>
          {VOICE_OPTIONS.map(({ key, icon: Icon, label, description }) => (
            <SettingsItem
              key={key}
              icon={Icon}
              label={label}
              description={description}
              onClick={() => requestToggleVoice(key)}
              rightElement={<ToggleSwitch checked={voicePrefs[key]} />}
            />
          ))}
          <p className="px-1 pt-1.5 text-[10px] text-gray-400 dark:text-slate-500 text-center">
            {voiceActive} de {VOICE_OPTIONS.length} opciones activas
          </p>
        </div>
      );
    }

    if (view === "pos-hardware") {
      return (
        <UserSettingsPosHardware canRunHardwareTests={canManagePosHardware} />
      );
    }

    if (view === "compliance") {
      return <UserSettingsCompliance />;
    }

    if (view === "storage") {
      return <UserSettingsStorage />;
    }

    if (view === "devices") {
      const { browser, os, isMobile } = getDeviceInfo();
      const DeviceIcon = isMobile ? Smartphone : Monitor;
      return (
        <div className="px-3 py-3 space-y-2">
          <p className="px-1 pb-0.5 text-[11px] text-gray-500 dark:text-slate-400 leading-relaxed">
            Sesiones abiertas con tu cuenta. Solo se puede cerrar sesiones
            remotas desde el backend.
          </p>

          {/* Sesión actual */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
              <DeviceIcon
                size={16}
                className="text-emerald-600 dark:text-emerald-400"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-bold text-gray-800 dark:text-slate-200 truncate">
                  {browser} · {os}
                </p>
                <span className="flex items-center gap-0.5 text-[9px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  <Wifi size={8} /> Activa
                </span>
              </div>
              <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-400 dark:text-slate-500">
                <Clock size={10} />
                <span>Este dispositivo · Sesión actual</span>
              </div>
            </div>
          </div>

          {/* Info de cuenta */}
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-600">
            <Shield
              size={12}
              className="text-gray-400 dark:text-slate-500 flex-shrink-0"
            />
            <p className="text-[11px] text-gray-500 dark:text-slate-400">
              Para cerrar otras sesiones activas, usa la administración de
              seguridad del sistema.
            </p>
          </div>

          {/* Diagnóstico de almacenamiento (soporte técnico) */}
          <button
            type="button"
            onClick={async () => {
              try {
                const report = await printHealthReport();
                const div = report.consistency.divergences.length;
                const pend = report.wal.pendingWrites;
                toast.success(
                  `Almacenamiento revisado: ${report.mirror.lsBusinessKeyCount} claves locales, ` +
                    `${report.mirror.idbKeyCount} en respaldo. ` +
                    (div === 0 && pend === 0
                      ? "Todo íntegro."
                      : `${div} divergencia(s), ${pend} escritura(s) pendiente(s). Ver consola.`),
                  { duration: 6000 },
                );
              } catch {
                toast.error("No se pudo generar el diagnóstico.");
              }
            }}
            className="w-full flex items-center gap-2 p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <Activity
              size={14}
              className="text-blue-500 dark:text-blue-400 flex-shrink-0"
            />
            <span className="text-[11px] font-bold text-blue-700 dark:text-blue-300 text-left">
              Diagnóstico de almacenamiento
              <span className="block font-normal text-[10px] text-blue-500/80 dark:text-blue-400/70">
                Verifica que no se hayan perdido datos (soporte)
              </span>
            </span>
          </button>
        </div>
      );
    }

    // Vista principal
    return (
      <div className="divide-y divide-gray-100 dark:divide-slate-700">
        {/* Preferencias */}
        <div className="px-3 py-2">
          <p className="px-1 pb-1.5 text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500 flex items-center gap-1.5">
            <Settings size={9} />
            Preferencias
          </p>
          <SettingsItem
            icon={theme === "dark" ? Sun : Moon}
            label="Apariencia"
            description={`Tema actual: ${theme === "dark" ? "Oscuro 🌙" : "Claro ☀️"}`}
            onClick={toggleTheme}
            rightElement={<ToggleSwitch checked={theme === "dark"} />}
          />
          <SettingsItem
            icon={Bell}
            label="Notificaciones"
            description="Configura alertas y avisos del sistema"
            onClick={() => setView("notifications")}
          />
          <SettingsItem
            icon={Mic}
            label="Voz y sonido"
            description="Sonidos de la app y voz (funciones futuras)"
            onClick={() => setView("voice")}
          />
          {canManagePosHardware ? (
            <SettingsItem
              icon={Printer}
              label="POS, gaveta y seguridad de caja"
              description={
                isOwner
                  ? "Solo el dueño del negocio — térmica, gaveta y tope de efectivo global"
                  : "Térmica, gaveta y tope de efectivo (dueño o administrador)"
              }
              onClick={() => setView("pos-hardware")}
            />
          ) : null}
          <SettingsItem
            icon={Smartphone}
            label="Dispositivos"
            description="Gestionar sesiones activas"
            onClick={() => setView("devices")}
          />
          <SettingsItem
            icon={HardDrive}
            label="Almacenamiento y respaldo"
            description="Espacio disponible, respaldo cifrado y restablecimiento"
            onClick={() => setView("storage")}
          />
          {isOwner && (
            <SettingsItem
              icon={FileText}
              label="Documentales y Cumplimiento Legal"
              description="Bóveda digital de documentos del negocio — RUT, uso de suelos, etc."
              onClick={() => setView("compliance")}
            />
          )}
        </div>

        {/* Seguridad */}
        <div className="px-3 py-2">
          <p className="px-1 pb-1.5 text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500 flex items-center gap-1.5">
            <Shield size={9} />
            Seguridad
          </p>
          <SettingsItem
            icon={Key}
            label="Cambiar contraseña"
            description="Actualiza tu contraseña de acceso"
            onClick={() => setView("change-password")}
          />
          <SettingsItem
            icon={Shield}
            label="Permisos activos"
            description={`Rol actual: ${roleLabel} · Acceso según privilegios`}
            disabled
          />
        </div>

        {/*
          ── SECCIÓN NEGOCIO ELIMINADA ──────────────────────────────────────
          - "Mi empresa" solo mostraba el businessId (dato técnico, no útil para el usuario)
          - "Editar perfil" deshabilitado por decisión arquitectónica (ver JSDoc arriba)
          - Foto de perfil también descartada (requiere storage externo + costos)
          - Se reactivará cuando el backend esté conectado y el dueño/admin
            gestione los datos de empleados desde un panel de administración
          ──────────────────────────────────────────────────────────────────
        */}
      </div>
    );
  };

  // ─── Modal ───────────────────────────────────────────────────────────────────

  const modal = (
    <>
      <ConfirmationModal
        isOpen={notifConfirm.open}
        onClose={closeNotifConfirm}
        onConfirm={confirmDisableNotif}
        title={notifConfirmCopy.title}
        message={notifConfirmCopy.message}
        confirmText="Desactivar"
        cancelText="Conservar"
        variant={notifConfirm.key === "sistema" ? "warning" : "info"}
      />
      <ConfirmationModal
        isOpen={voiceConfirm.open}
        onClose={closeVoiceConfirm}
        onConfirm={confirmDisableVoice}
        title={voiceConfirmCopy.title}
        message={voiceConfirmCopy.message}
        confirmText="Desactivar"
        cancelText="Conservar"
        variant={voiceConfirm.key === "sonidos_ui" ? "warning" : "info"}
      />
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* Modal centrado */}
      <div
        className={`fixed inset-0 z-[101] flex items-center justify-center p-4 transition-all duration-200 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        <div
          className={`w-full ${view === "pos-hardware" || view === "storage" ? "max-w-md" : "max-w-sm"} bg-white dark:bg-slate-800 rounded-2xl shadow-2xl shadow-black/20 dark:shadow-black/60 overflow-hidden transition-all duration-200 ${isOpen ? "scale-100 translate-y-0" : "scale-95 translate-y-4"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="relative px-4 pt-4 pb-3 bg-gradient-to-br from-primary-600 to-primary-800">
            {view !== "main" ? (
              /* Sub-vista: botón volver + título */
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setView("main")}
                  className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <ChevronLeft size={15} />
                </button>
                <h2 className="text-sm font-bold text-white flex-1">
                  {SUB_VIEW_TITLES[view]}
                </h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <X size={15} />
                </button>
              </div>
            ) : (
              /* Vista principal: info de usuario */
              <>
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <X size={15} />
                </button>

                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-white text-lg font-black border-2 border-white/30 shadow">
                      {initial}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
                  </div>

                  <div className="min-w-0">
                    <h2 className="text-base font-black text-white truncate">
                      {displayName}
                    </h2>
                    <p className="text-xs text-primary-200 truncate">
                      {user?.email}
                    </p>
                    <span
                      className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${roleColor}`}
                    >
                      {roleLabel}
                    </span>
                  </div>
                </div>

                <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              </>
            )}
          </div>

          {/* ── Contenido con scroll ── */}
          <div
            className={`overflow-y-auto custom-scrollbar ${view === "pos-hardware" || view === "storage" ? "max-h-[85vh]" : "max-h-[60vh]"}`}
          >
            {renderBody()}
          </div>

          {/* ── Footer: cerrar sesión (solo en vista main) ── */}
          {view === "main" && (
            <div className="px-3 py-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80">
              <button
                onClick={() => {
                  onClose();
                  handleLogout();
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 font-semibold text-xs transition-all active:scale-[0.98]"
              >
                <LogOut size={14} />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
};
